import React, { Fragment, PureComponent } from "react";
import { ToastContainer, toast } from "react-toastify";
import { UIView } from "@uirouter/react";
import { connect } from "react-redux";
import _ from "lodash";
import PropTypes from "prop-types";
import i18n from "i18next";
import moment from "moment";

import { ErrorToast, Modal } from "./Common";
import { checkIsExternalUser, hasUserAtLeastOneRole } from "../helpers/Authorization/authorizationHelpers";
import {
  getNotifications,
  getUnitOfMeasurements,
  initializeFirebaseApp,
  reinitializeReportThisIssueSendSucceeded,
  reportThisIssue,
  setError,
  setScreenSize,
  updateUserMetadata
} from "../actions";
import { getViewStateFromUrl } from "../helpers/urlHelpers";
import LandscapeWarning from "./Errors/LandscapeWarning";
import MobileNavigation from "./Layout/SortedList/MobileNavigation";
import Navbar from "./Layout/Navbar";
import SecondaryNavbar from "./Layout/SecondaryNavbar";

class App extends PureComponent {
  static propTypes = {
    getNotifications: PropTypes.func.isRequired,
    getUnitOfMeasurements: PropTypes.func.isRequired,
    initializeFirebaseApp: PropTypes.func.isRequired,
    reinitializeReportThisIssueSendSucceeded: PropTypes.func.isRequired,
    reportThisIssue: PropTypes.func.isRequired,
    reportThisIssueSucceeded: PropTypes.bool.isRequired,
    setError: PropTypes.func.isRequired,
    setScreenSize: PropTypes.func.isRequired,
    transition: PropTypes.object.isRequired,
    updateUserMetadata: PropTypes.func.isRequired,
    errorCode: PropTypes.string,
    errorCorrelationId: PropTypes.string,
    errorStack: PropTypes.string,
    errorTimestamp: PropTypes.string,
    isActionInProgress: PropTypes.bool,
    isMobile: PropTypes.bool,
    isUserMetadataFetched: PropTypes.bool,
    routerStateName: PropTypes.string,
    user: PropTypes.object,
    userMetadata: PropTypes.object
  };

  static defaultProps = {
    errorCode: "",
    errorStack: "",
    errorCorrelationId: "",
    errorTimestamp: "",
    isActionInProgress: false,
    isMobile: false,
    isUserMetadataFetched: false,
    routerStateName: "",
    user: {},
    userMetadata: {}
  };

  state = {
    currentPage: "",
    scrollStack: [],
    wasLandscapeWarningDismissed: false,
    showPrivacyPolicy: false,
    wasStatePopped: false
  };

  componentDidMount() {
    this.handleSetScreenSize();
    window.addEventListener("resize", this.handleSetScreenSize);
    window.addEventListener("popstate", this.handlePopState);
  }

  componentDidUpdate(prevProps, _prevState, snapshot) {
    if (!prevProps.isUserMetadataFetched && this.props.isUserMetadataFetched) {
      const email = this.props.user.email || this.props.user.sub;
      this.setNewRelicCustomAttribute(email);
      if (hasUserAtLeastOneRole()) {
        this.props.getUnitOfMeasurements();
        this.props.initializeFirebaseApp(email, this.handleGetNotifications);
      }
    }

    if (!prevProps.errorCode && this.props.errorCode) {
      if (this.props.errorCode === "404") {
        this.redirectToErrorPage(this.props.errorCode);
        this.removeError();
      } else {
        const errorComponent = <ErrorToast message={this.props.errorCode} onReportClick={this.handleReportThisIssue} />;
        toast.error(errorComponent, { autoClose: 10000, onClose: this.removeError });
      }
    }

    if (snapshot) {
      this.handleAddToScrollStack(snapshot, prevProps.routerStateName);
    }

    if (!this.props.isActionInProgress && prevProps.isActionInProgress && this.state.wasStatePopped) {
      this.handleScrollScreenOnPopState();
    }

    if (!prevProps.reportThisIssueSucceeded && this.props.reportThisIssueSucceeded) {
      toast(i18n.t("contactUs.reportIssueSucceeded"), { type: toast.TYPE.SUCCESS });
      this.props.reinitializeReportThisIssueSendSucceeded();
    }

    this.handleGetCurrentPage();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleSetScreenSize);
    window.removeEventListener("popstate", this.handlePopState);
  }

  get shouldShowTermsAndConditions() {
    const { isUserMetadataFetched, userMetadata } = this.props;
    const acceptedTermsAndConditionsDate = moment(userMetadata.hasAcceptedTermsAndConditions, "YYYY-MM-DD", true);
    return isUserMetadataFetched && checkIsExternalUser() && !acceptedTermsAndConditionsDate.isValid();
  }

  getSnapshotBeforeUpdate(prevProps) {
    if (prevProps.routerStateName !== this.props.routerStateName) {
      return {
        scrollTop: this.panelContainerRef.current.scrollTop,
        routerStateName: prevProps.routerStateName,
        routerParams: getViewStateFromUrl(prevProps.routerParams)
      };
    }

    return null;
  }

  setNewRelicCustomAttribute = email => {
    window.newrelic.addPageAction(hasUserAtLeastOneRole() ? "LoggedUser" : "UnauthorizedLoggedUser", { email });
  };

  panelContainerRef = React.createRef();

  handleDismissLandscapeWarning = () => this.setState({ wasLandscapeWarningDismissed: true });

  handleAcceptPrivacyPolicy = () => this.setState({ showPrivacyPolicy: false });

  handleGetNotifications = () => this.props.getNotifications();

  handlePopState = () => this.setState({ wasStatePopped: true });

  handleAddToScrollStack = (snapshot, prevRouterStateName) => {
    const newScrollStack = [...this.state.scrollStack];
    const existingScrollIndex = newScrollStack.findIndex(scroll => scroll.routerStateName === prevRouterStateName);
    if (existingScrollIndex !== -1) {
      newScrollStack[existingScrollIndex] = snapshot;
    } else {
      newScrollStack.push(snapshot);
    }

    this.setState({ scrollStack: newScrollStack });
  };

  handleScrollScreenOnPopState = () => {
    const scrollInfo = this.state.scrollStack.find(scroll => scroll.routerStateName === this.props.routerStateName);
    if (scrollInfo) {
      this.panelContainerRef.current.scrollTop = scrollInfo.scrollTop;
    }

    this.setState({ wasStatePopped: false });
  };

  handleSetScreenSize = () => this.props.setScreenSize(window.innerWidth);

  redirectToErrorPage = errorCode => this.props.transition.router.stateService.go(errorCode);

  removeError = () => this.props.setError(null);

  handleAcceptTermsAndConditions = () => {
    this.setState({ showPrivacyPolicy: true });
    const { user, userMetadata } = this.props;
    this.props.updateUserMetadata({ ...userMetadata, hasAcceptedTermsAndConditions: moment().format("YYYY-MM-DD") }, user.sub);
  };

  handleReportThisIssue = () => {
    const { cookies, flash, ip, java, scripts, timestamp, websockets, ...browserReport } = window.browserReportSync(); // eslint-disable-line
    const fullMessage = {
      sendTo: "helpDesk",
      messageType: "report",
      message: i18n.t("contactUs.reportAnIssue"),
      senderName: this.props.user.name,
      senderEmail: this.props.user.email,
      issue: JSON.stringify({
        browserReport,
        href: window.location.href,
        error: this.props.errorCode,
        stack: this.props.errorStack,
        correlationId: this.props.errorCorrelationId,
        timestamp: this.props.errorTimestamp
      })
    };
    this.props.reportThisIssue(fullMessage);
  };

  handleGetCurrentPage = () => {
    setTimeout(() => this.setState({ currentPage: this.panelContainerRef.current.lastChild.classList[1] }), 10);
  };

  termsAndConditionButtons = [
    {
      onClose: this.handleAcceptTermsAndConditions,
      label: i18n.t("usage.accept")
    }
  ];

  privacyPolicyButtons = [
    {
      onClose: this.handleAcceptPrivacyPolicy,
      label: i18n.t("usage.accept")
    }
  ];

  render() {
    return (
      <Fragment>
        {this.props.isMobile && !this.state.wasLandscapeWarningDismissed && <LandscapeWarning onClose={this.handleDismissLandscapeWarning} />}

        {this.shouldShowTermsAndConditions && (
          <Modal
            buttons={this.termsAndConditionButtons}
            message={i18n.t("usage.termsAndConditionsContent")}
            title={i18n.t("usage.termsAndConditionsTitle")}
            onClose={this.handleAcceptTermsAndConditions}
          />
        )}

        {this.state.showPrivacyPolicy && (
          <Modal
            buttons={this.privacyPolicyButtons}
            message={i18n.t("usage.privacyPolicyContent")}
            title={i18n.t("usage.privacyPolicyTitle")}
            onClose={this.handleAcceptPrivacyPolicy}
          />
        )}

        <Navbar />
        <SecondaryNavbar onItemClick={this.handlePopState} scrollStack={this.state.scrollStack} />
        <div id="panelContainer" ref={this.panelContainerRef} className="panel__container">
          <UIView />
        </div>
        {this.props.isMobile && <MobileNavigation currentPage={this.state.currentPage} />}

        <ToastContainer
          className="toast-on-top"
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnVisibilityChange
          draggable
          pauseOnHover
        />
      </Fragment>
    );
  }
}

const mapStateToThis = state => {
  return {
    isActionInProgress: !_.isEmpty(state.app.pendingActions),
    routerParams: state.app.routerParams,
    errorCode: state.app.errorCode,
    errorStack: state.app.errorStack,
    errorCorrelationId: state.app.errorCorrelationId,
    errorTimestamp: state.app.errorTimestamp,
    routerStateName: state.app.routerState.name,
    reportThisIssueSucceeded: state.contactUs.reportThisIssueSucceeded,
    user: state.app.user,
    isMobile: state.app.isMobile,
    isUserMetadataFetched: state.app.isUserMetadataFetched,
    userMetadata: state.app.userMetadata
  };
};

export default connect(
  mapStateToThis,
  {
    getNotifications,
    getUnitOfMeasurements,
    initializeFirebaseApp,
    reinitializeReportThisIssueSendSucceeded,
    reportThisIssue,
    setError,
    setScreenSize,
    updateUserMetadata
  }
)(App);



// WEBPACK FOOTER //
// ./src/components/App.js